import { Search } from 'lucide-react'
import { useId, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/Input'
import { Kbd } from '@/components/ui/Kbd'
import { Spinner } from '@/components/ui/Spinner'
import { cn } from '@/utils/cn'

interface SearchComboboxProps<T> {
  label: string
  placeholder?: string
  value: string
  onValueChange: (value: string) => void
  items: T[] | undefined
  loading?: boolean
  getKey: (item: T) => string | number
  renderItem: (item: T) => ReactNode
  /** Reason an item cannot be picked (shown instead of selecting), or null. */
  disabledReason?: (item: T) => string | null
  onSelect: (item: T) => void
  onEscape?: () => void
  emptyText?: string
  inputRef?: Ref<HTMLInputElement>
  autoFocus?: boolean
  hint?: ReactNode
}

/**
 * Keyboard-first search box (WAI-ARIA combobox + listbox): type, ↑/↓ to move, Enter to pick,
 * Esc to clear. The results list is inline (not a popover) so the desk layout never jumps.
 */
export function SearchCombobox<T>({
  label,
  placeholder,
  value,
  onValueChange,
  items,
  loading,
  getKey,
  renderItem,
  disabledReason,
  onSelect,
  onEscape,
  emptyText,
  inputRef,
  autoFocus,
  hint,
}: SearchComboboxProps<T>) {
  const { t } = useTranslation('library')
  const id = useId()
  const listId = `${id}-list`
  const [active, setActive] = useState(0)
  const results = value.trim() ? (items ?? []) : []
  const open = value.trim().length > 0
  const current = Math.min(active, Math.max(0, results.length - 1))

  const pick = (item: T | undefined) => {
    if (item === undefined) return
    if (disabledReason?.(item)) return
    onSelect(item)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(results.length - 1, Math.min(i, results.length - 1) + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(0, Math.min(i, results.length - 1) - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      pick(results[current])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      if (value) onValueChange('')
      else onEscape?.()
    }
  }

  return (
    <div>
      <label htmlFor={`${id}-input`} className="text-[0.8125rem] font-medium text-ink-soft">
        {label}
      </label>
      <div className="mt-1.5">
        <Input
          ref={inputRef}
          id={`${id}-input`}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && results[current] !== undefined ? `${id}-opt-${current}` : undefined}
          autoComplete="off"
          autoFocus={autoFocus}
          size="lg"
          value={value}
          placeholder={placeholder}
          onChange={(e) => {
            setActive(0)
            onValueChange(e.target.value)
          }}
          onKeyDown={onKeyDown}
          leading={<Search />}
          trailing={loading ? <Spinner className="size-4 text-brand-600" label={t('Searching')} /> : null}
        />
      </div>
      {hint ? (
        <p className="mt-1.5 flex flex-wrap items-center gap-1 text-2xs text-muted">{hint}</p>
      ) : (
        <p className="mt-1.5 flex flex-wrap items-center gap-1 text-2xs text-muted">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> {t('to move')} · <Kbd>{t('Enter')}</Kbd> {t('to choose')} · <Kbd>{t('Esc')}</Kbd>{' '}
          {t('to clear')}
        </p>
      )}
      <ul id={listId} role="listbox" aria-label={label} className={cn('mt-2 space-y-1', !open && 'hidden')}>
        {open && !loading && results.length === 0 ? (
          <li className="rounded-control px-3 py-2.5 text-sm text-muted">{emptyText ?? t('No matches.')}</li>
        ) : null}
        {results.map((item, i) => {
          const reason = disabledReason?.(item) ?? null
          return (
            // Keyboard selection is handled by the combobox input (aria-activedescendant pattern).
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={getKey(item)}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={i === current}
              aria-disabled={reason ? true : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(item)}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-control border border-transparent px-3 py-2 transition-colors',
                i === current ? 'border-brand-200 bg-brand-50/70' : 'hover:bg-surface-muted',
                reason && 'cursor-not-allowed opacity-60',
              )}
            >
              <div className="min-w-0 flex-1">{renderItem(item)}</div>
              {reason ? (
                <span className="shrink-0 text-2xs font-medium text-warning-700">{reason}</span>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
